import { api } from "./api";

export type ReasoningLevel = "low" | "medium";

export interface StreamOptions {
  projectId: string;
  message: string;
  reasoning?: ReasoningLevel;
  images?: Array<string | { mime: string; b64: string }>;
  conversationId?: string;
  messageId?: string;
  previousResponseId?: string; // OpenAI's response ID from last message
  conversationHistory?: Array<{ role: string; content: string }>; // Fallback context when response ID is invalid
  // Limit retrieval to these interview IDs (server will filter vector store)
  interviewIds?: string[];
}

export async function streamChat(
  options: StreamOptions,
  onDelta: (chunk: string) => void,
  onError?: (err: string) => void,
  onComplete?: (responseId?: string) => void,
  abortSignal?: AbortSignal
): Promise<void> {
  try {
    const endpoint = `${api.getBaseUrl()}/api/chat/respond`;
    const resp = await fetch(endpoint, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      },
      body: JSON.stringify(options),
      signal: abortSignal,
    });

    if (!resp.ok || !resp.body) {
      onError?.(`HTTP ${resp.status}`);
      return;
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder("utf-8");

    let buffer = "";
    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";

        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();

          if (payload === "[DONE]") {
            return;
          }
          try {
            const obj = JSON.parse(payload);
            if (obj.delta) onDelta(obj.delta);
            if (obj.error) {
              const msg =
                typeof obj.error === "string"
                  ? obj.error
                  : obj.error?.message || "Unknown error";
              onError?.(msg);
            }
            if (obj.completed && onComplete) {
              onComplete(obj.response_id);
            }
          } catch {
            // Ignore malformed lines
          }
        }
      }
    } finally {
      // Always clean up the reader
      reader.releaseLock();
    }
  } catch (error: any) {
    // Handle abort errors gracefully - don't call onError for user-initiated cancellations
    if (error.name === "AbortError") {
      console.log("Chat request aborted by user");
      return;
    }
    // For other errors, notify via onError callback
    onError?.(error.message || "Connection error");
  }
}
